/**
 * ImageWithDownload Component
 *
 * Wraps markdown-rendered images with a hover download control.
 * Used primarily for assistant message content.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import classes from './ImageWithDownload.module.css';
import { trackComponentInteraction } from '../../../../../utils/analytics';

export interface ImageWithDownloadProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {}

export const ImageWithDownload: React.FC<ImageWithDownloadProps> = ({
  src,
  alt,
  className,
  onLoad,
  onError,
  ...imgProps
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useLayoutEffect(() => {
    const img = imgRef.current;
    setHasError(false);

    if (!img || !src) {
      setIsLoaded(false);
      return;
    }

    setIsLoaded(img.complete && img.naturalWidth > 0);
  }, [src]);

  const handleLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
    setIsLoaded(true);
    onLoad?.(event);
  };

  const handleError: React.ReactEventHandler<HTMLImageElement> = (event) => {
    setHasError(true);
    onError?.(event);
  };

  const showLoadingOverlay = Boolean(src) && !isLoaded && !hasError;

  const handleDownload = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!src) return;

    // Track Code Interpreter output interaction (download/expand chart)
    trackComponentInteraction(
      'ChatMessage',
      'Code Interpreter Output',
      'button',
      'click',
      'code_interpreter_output_interact'
    );

    try {
      const link = document.createElement('a');
      link.href = src;

      // Try to infer a reasonable filename from the URL
      try {
        const url = new URL(src, window.location.origin);
        const pathname = url.pathname || '';
        const filename =
          pathname.split('/').filter(Boolean).pop() || 'image.png';
        link.download = filename;
      } catch {
        link.download = 'image.png';
      }

      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      // Fallback: open in a new tab if download attribute is blocked
      window.open(src, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <span className={classes.imageWrapper}>
      {showLoadingOverlay && <span className={classes.loadingOverlay} aria-hidden="true" />}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        {...imgProps}
        onLoad={handleLoad}
        onError={handleError}
        className={`${classes.image} ${showLoadingOverlay ? classes.imageLoading : classes.imageLoaded} ${className || ''}`}
      />
      {isLoaded && !hasError && src && (
        <Tooltip label="Download" position="top" withinPortal>
          <button
            type="button"
            className={classes.downloadOverlay}
            aria-label="Download image"
            onClick={handleDownload}
          >
            <span className={classes.downloadIcon}>
              <IconDownload size={18} />
            </span>
            <span className={classes.downloadLabel}>Download</span>
          </button>
        </Tooltip>
      )}
    </span>
  );
};
